import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Grid } from '@mui/material';
import { Alert, SelectSearchField } from 'vetrf-ui';
import { PatentHolder, UserData } from 'components/UserDataContext/types';
import { BusinessEntityWrapper, SupervisedObjectWrapper } from 'types/types';
import { filterSupervisedObjects } from './utils';

type WorkParamsFieldsProps = {
  userData: UserData | null;
  loading: boolean;
};

export const WorkParamsFields = ({ userData, loading }: WorkParamsFieldsProps) => {
  const [currentBusinessEntityOptions, setCurrentBusinessEntityOptions] = useState<PatentHolder[]>(
    []
  );
  const [currentSupervisedObjectOptions, setCurrentSupervisedObjectOptions] = useState<
    SupervisedObjectWrapper[]
  >([]);
  const firstRender = useRef(true);
  const { getValues, setValue } = useFormContext();

  const currentSupervisedObject = useMemo(
    () =>
      userData?.currentSupervisedObject
        ? {
            ...userData?.currentSupervisedObject,
            name: `${userData?.currentSupervisedObject.supervisedObject.name} [${userData?.currentSupervisedObject.supervisedObject.regNumber}]`,
          }
        : undefined,
    [userData]
  );

  useWatch({ name: 'currentBusinessEntity' });
  useWatch({ name: 'currentSupervisedObject' });
  const currentBusinessEntityValue: BusinessEntityWrapper = getValues('currentBusinessEntity');
  const currentSupervisedObjectValue: SupervisedObjectWrapper =
    getValues('currentSupervisedObject');

  useEffect(() => {
    setCurrentBusinessEntityOptions(userData?.patentHolders || []);
    if (!userData?.supervisedObjects) {
      setCurrentSupervisedObjectOptions([]);
    } else {
      const formattedOptions = userData?.supervisedObjects.map((el) => ({
        ...el,
        name: `${el.supervisedObject.name} [${el.supervisedObject.regNumber}]`,
      }));
      setCurrentSupervisedObjectOptions(formattedOptions);
    }
  }, [userData]);

  const setSupervisedObjectOptions = () => {
    const res = filterSupervisedObjects(userData, currentBusinessEntityValue?.id).map((el) => ({
      ...el,
      name: `${el.supervisedObject.name} [${el.supervisedObject.regNumber}]`,
    }));

    setCurrentSupervisedObjectOptions(res);
    if (res.length === 1) {
      setValue('currentSupervisedObject', res[0]);
    }
  };

  useEffect(() => {
    if (firstRender.current && currentBusinessEntityValue) {
      firstRender.current = false;
      setSupervisedObjectOptions();
    } else if (currentBusinessEntityValue) {
      setValue('currentSupervisedObject', null);
      setSupervisedObjectOptions();
    }
  }, [currentBusinessEntityValue, firstRender]);

  return (
    <>
      <SelectSearchField
        disabled={loading}
        fieldClassName="col-xs-12 selectpicker"
        labelClassName="col-xs-12 control-label text-align-left"
        labelKey="shortName"
        valueKey="id"
        labelText="Хозяйствующий субъект"
        placeholder=""
        name="currentBusinessEntity"
        options={currentBusinessEntityOptions}
        defaultValue={userData?.currentBusinessEntity || currentBusinessEntityOptions[0]}
      />
      {currentBusinessEntityValue && (
        <Grid item xs={12}>
          <Alert messageType="info">
            <div>{currentBusinessEntityValue.businessEntity?.firmName}</div>
            <small>
              {currentBusinessEntityValue.businessEntity?.juridicalAddress?.addressView}
            </small>
          </Alert>
        </Grid>
      )}
      <SelectSearchField
        clearable
        fieldClassName="col-xs-12 selectpicker"
        labelClassName="col-xs-12 control-label text-align-left"
        labelKey="name"
        valueKey="id"
        labelText="Поднадзорный объект"
        placeholder=""
        name="currentSupervisedObject"
        disabled={!currentSupervisedObjectOptions.length || loading}
        options={currentSupervisedObjectOptions}
        defaultValue={currentSupervisedObject}
      />
      {currentSupervisedObjectValue && (
        <Grid item xs={12}>
          <Alert messageType="info">
            <div>
              {currentSupervisedObjectValue.supervisedObject?.name}
              <span>{` [номер: ${currentSupervisedObjectValue.supervisedObject?.regNumber}]`}</span>
            </div>
            <small>
              {currentSupervisedObjectValue.supervisedObject?.activityLocation?.address}
            </small>
          </Alert>
        </Grid>
      )}
    </>
  );
};
